import { useMemo } from 'react';
import { groupBy } from 'lodash';
import { Note } from '@models/note';
import { QuoteRating } from '@models/quoteRating';
import { QuoteStatusChangeItem } from '@models/quoteStatusChange';
import {
  NewDocumentItem,
  QuoteActivityItemType,
  ratingText,
  statusText,
} from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivityItem.constants';
import {
  DateHeaderLabel,
  IFilteringItem,
  Sorting,
  getDateHeaderLabel,
} from '@pages/QuoteDetails/sidebarItems/summary/quoteActivity/QuoteActivity.constants';
import { UnderwritingActivityItem } from '@models/underwritingReferral';

export interface QuoteActivityData {
  allStatusChanges: QuoteStatusChangeItem[] | undefined;
  allQuoteRatings: QuoteRating[] | undefined;
  allNotes: Note[] | undefined;
  allDocuments: NewDocumentItem[] | undefined;
  allQuoteUnderwritingActivity: UnderwritingActivityItem[] | undefined;
  quote?: {
    id: string;
    createdAt: string;
    createdByUser?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

export interface QuoteActivityFilters {
  filter: { list: IFilteringItem[] };
  sorting: { type: Sorting };
  textInput: string;
}

export const useQuoteActivityData = (data: QuoteActivityData, filters: QuoteActivityFilters) => {
  const { allStatusChanges, allQuoteRatings, allNotes, allDocuments, allQuoteUnderwritingActivity } = data;
  const { filter, sorting, textInput } = filters;

  const items = useMemo(() => {
    const allItems = [];

    if (allStatusChanges) {
      allItems.push(
        ...allStatusChanges.map((el) => ({
          ...el,
          activityItemType: QuoteActivityItemType.QuoteStatusChange,
        })),
      );
    }
    if (allQuoteRatings) {
      allItems.push(
        ...allQuoteRatings.map((el) => ({
          ...el,
          activityItemType: QuoteActivityItemType.QuoteRating,
        })),
      );
    }
    if (allNotes) {
      allItems.push(
        ...allNotes.map((el) => ({
          ...el,
          activityItemType: el.type === 'Note' ? QuoteActivityItemType.Note : QuoteActivityItemType.Message,
        })),
      );
    }
    if (allDocuments) {
      allItems.push(
        ...allDocuments.map((el) => ({
          ...el,
          activityItemType: QuoteActivityItemType.Document,
        })),
      );
    }

    if (allQuoteUnderwritingActivity) {
      allItems.push(
        ...allQuoteUnderwritingActivity.map((el) => ({
          ...el,
          activityItemType: QuoteActivityItemType.UnderwritingActivity,
        })),
      );
    }

    if (allItems.length > 0) {
      return allItems
        .flat()
        .filter((el) => {
          const index = filter.list.findIndex((element: IFilteringItem) => {
            return element.activityItemType === el.activityItemType;
          });
          return filter.list[index]?.checked;
        })
        .sort((a, b) => {
          if (sorting.type === Sorting.Newest) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          }
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        })
        .filter((el) => {
          if (textInput !== '') {
            const lowerCaseText = textInput.toLowerCase();
            let itemText = '';
            if (Object.hasOwn(el, 'body') && 'body' in el) {
              itemText = el.body.toLowerCase();
            }
            if (Object.hasOwn(el, 'newQuoteStatus') && 'newQuoteStatus' in el) {
              itemText = `${statusText} ${el.newQuoteStatus.label}`.toLowerCase();
            }
            if (Object.hasOwn(el, 'termPremium') && 'termPremium' in el) {
              itemText = ratingText[el.status](el.termPremium, el?.termTaxFeeSurcharge);
            }
            return itemText.includes(lowerCaseText);
          }
          return el;
        });
    }
    return [];
  }, [
    allNotes,
    allStatusChanges,
    allQuoteUnderwritingActivity,
    textInput,
    filter.list,
    allQuoteRatings,
    allDocuments,
    sorting.type,
  ]);

  const dateHeaderItems = useMemo(() => {
    const itemsByDateHeader = groupBy(items, (item) => {
      return getDateHeaderLabel(new Date(item.createdAt));
    });
    const itemsByDateHeaderIds = {
      [DateHeaderLabel.Today]: itemsByDateHeader[DateHeaderLabel.Today]?.[0].id,
      [DateHeaderLabel.Yesterday]: itemsByDateHeader[DateHeaderLabel.Yesterday]?.[0].id,
      [DateHeaderLabel.PastWeek]: itemsByDateHeader[DateHeaderLabel.PastWeek]?.[0].id,
      [DateHeaderLabel.PastMonth]: itemsByDateHeader[DateHeaderLabel.PastMonth]?.[0].id,
      [DateHeaderLabel.Older]: itemsByDateHeader[DateHeaderLabel.Older]?.[0].id,
    };
    return itemsByDateHeaderIds;
  }, [items]);

  const lastLoadedItems = useMemo(() => {
    return {
      [QuoteActivityItemType.QuoteStatusChange]: allStatusChanges?.slice(-1),
      [QuoteActivityItemType.QuoteRating]: allQuoteRatings?.slice(-1),
      [QuoteActivityItemType.Note]: allNotes?.slice(-1),
      [QuoteActivityItemType.Document]: allDocuments?.slice(-1),
    };
  }, [allNotes, allDocuments, allQuoteRatings, allStatusChanges]);

  return {
    items,
    dateHeaderItems,
    lastLoadedItems,
  };
};
